import { RefreshCw } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { useCanManageGbp } from '@/hooks/use-can-manage-gbp';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { gbpConnectHref } from '@/lib/api-paths';
import { formatUpdatedAgo } from '@/lib/format-relative-time';
import type { GbpPartError } from '@/lib/types';

// Shared per-section status affordances for the GBP screen (design brief
// §5.6). 'unauthorized' is DURABLE — the customer revoked access at Google —
// and gets an unmissable reconnect call regardless of whether stale data is
// also showing underneath it. network/quota/upstream/forbidden/not_found/
// rejected are self-healing (or, for 'rejected' on a read, our own bug — not
// something the owner can act on) and must never get that same urgent
// treatment (Task 9's carry-forward warning to Tasks 10/15).

export function PartErrorBanner({
  siteId,
  what,
  error,
  hasStaleData,
}: {
  siteId: string;
  what: string;
  error: GbpPartError;
  hasStaleData: boolean;
}) {
  // Reconnecting is owner-only (gbp:manage), same as connecting.
  const businessId = useBusinessId();
  const canManage = useCanManageGbp();
  if (error === 'unauthorized') {
    return (
      <div className="mb-4 flex flex-col gap-3 rounded-md bg-danger-bg px-4 py-3 text-sm text-danger-fg sm:flex-row sm:items-center sm:justify-between">
        <span>
          {canManage ? 'Your Google account disconnected' : 'Google access was disconnected for'}{' '}
          {what}
          {hasStaleData ? ' — showing what we last had.' : '.'}{' '}
          {canManage ? 'Reconnect to keep it updating.' : 'The business owner can reconnect it.'}
        </span>
        {canManage && businessId ? (
          <a
            href={gbpConnectHref(businessId, siteId)}
            className={buttonVariants({ size: 'sm', variant: 'destructive' })}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Reconnect Google
          </a>
        ) : null}
      </div>
    );
  }
  return (
    <div className="mb-4 rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-fg">
      Could not refresh {what} right now.{hasStaleData ? ' Showing the last data we had.' : ''}
    </div>
  );
}

// Never renders when fetchedAt is undefined — an absent timestamp means "we
// don't actually know when this was last good," and inventing a stamp would
// break the same honesty rule as inventing a number (design brief §3).
export function StaleStamp({ fetchedAt }: { fetchedAt: number | undefined }) {
  if (fetchedAt === undefined) return null;
  return <p className="text-xs text-text-tertiary">{formatUpdatedAgo(fetchedAt)}</p>;
}
